import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  BarChart3,
  TrendingUp,
  Users,
  Calendar,
  X,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  generateExcelFile,
  generateWordDocument,
  downloadFile,
  generateReportData,
} from '../../../utils/fileDownloadUtils';

const GenerateReportAction = () => {
  const [showModal, setShowModal] = useState(false);
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('30days');
  const [isGenerating, setIsGenerating] = useState(false);

  const reportTypes = [
    {
      id: 'overview',
      name: 'System Overview',
      description: 'General system health and metrics',
      icon: BarChart3,
      color: 'blue',
    },
    {
      id: 'users',
      name: 'User Analytics',
      description: 'User engagement and activity metrics',
      icon: Users,
      color: 'green',
    },
    {
      id: 'performance',
      name: 'Performance Report',
      description: 'System performance and optimization',
      icon: TrendingUp,
      color: 'purple',
    },
    {
      id: 'comprehensive',
      name: 'Comprehensive Report',
      description: 'Full system analysis and recommendations',
      icon: FileText,
      color: 'orange',
    },
  ];

  const dateRanges = [
    { value: '7days', label: 'Last 7 days' },
    { value: '30days', label: 'Last 30 days' },
    { value: '90days', label: 'Last 90 days' },
    { value: '1year', label: 'Last year' },
    { value: 'custom', label: 'Custom range' },
  ];

  const handleGenerateReport = async format => {
    setIsGenerating(true);
    try {
      const reportData = await generateReportData(reportType, dateRange);
      const selectedReport = reportTypes.find(r => r.id === reportType);
      const selectedDateRange = dateRanges.find(d => d.value === dateRange);

      const dataArray = Object.entries(reportData).map(([key, value]) => ({
        metric: key,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        category: selectedReport.name,
        dateRange: selectedDateRange.label,
        generatedAt: new Date().toISOString(),
      }));

      let fileContent, filename, mimeType;

      if (format === 'excel') {
        fileContent = generateExcelFile(dataArray, selectedReport.name);
        filename = `${selectedReport.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else {
        fileContent = generateWordDocument(dataArray, selectedReport.name);
        filename = `${selectedReport.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        mimeType = 'text/html';
      }

      downloadFile(fileContent, filename, mimeType);

      toast.success(
        `${selectedReport.name} generated and downloaded in ${format.toUpperCase()} format for ${selectedDateRange.label}`
      );
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to generate report: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowModal(true)}
        className="flex flex-col items-center rounded-lg bg-gradient-to-br from-green-50 via-green-100 to-green-200 p-3 shadow-md transition-colors hover:bg-green-200 md:p-4"
      >
        <span className="mb-1.5 md:mb-2">
          <FileText className="h-5 w-5 text-green-700 md:h-6 md:w-6" />
        </span>
        <span className="text-xs font-medium text-green-900 md:text-sm">Generate Report</span>
      </motion.button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Generate Report</h3>
                  <p className="text-sm text-gray-600">Create comprehensive system reports</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="mb-3 block text-sm font-medium text-gray-700">Report Type</label>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {reportTypes.map(report => {
                    const IconComponent = report.icon;
                    const isSelected = reportType === report.id;

                    return (
                      <label
                        key={report.id}
                        className={`relative flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-all ${
                          isSelected
                            ? `border-${report.color}-500 bg-${report.color}-50`
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="reportType"
                          value={report.id}
                          checked={isSelected}
                          onChange={e => setReportType(e.target.value)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <IconComponent className={`h-5 w-5 text-${report.color}-600`} />
                            <span className="font-medium text-gray-900">{report.name}</span>
                          </div>
                          <p className="text-sm text-gray-600">{report.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Date Range</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <select
                    value={dateRange}
                    onChange={e => setDateRange(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {dateRanges.map(range => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-700">
                  Export Format
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-gray-300">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="excel"
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <FileSpreadsheet className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium text-gray-900">Excel</div>
                      <div className="text-xs text-gray-500">Spreadsheet format</div>
                    </div>
                  </label>
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-gray-300">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="word"
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <FileText className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Word</div>
                      <div className="text-xs text-gray-500">Document format</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-gray-700">Report Preview</h4>
                <div className="text-sm text-gray-600">
                  <p>• System metrics and performance data</p>
                  <p>• User engagement analytics</p>
                  <p>• Error logs and system health</p>
                  <p>• Recommendations and insights</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => handleGenerateReport('excel')}
                  disabled={isGenerating}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="h-4 w-4" />
                      Download Excel
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleGenerateReport('word')}
                  disabled={isGenerating}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" />
                      Download Word
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default GenerateReportAction;
